import type { EntityID, MappedThread, Thread, ThreadOnlyCurrentNoDeleted } from '@wovin/core'
import type { TiptapContent } from './VMs/TypeMap'
import { plainContentMatchSpecificTag } from '@note3/utils'
import {
	anyOf,
	isEncryptedApplog,
	joinThreads,
	lastWriteWins,
	query,
	queryAndMap,
	removeDuplicateAppLogs,
	rollingFilter,
	withoutDeleted,
} from '@wovin/core'
import { Logger } from 'besonders-logger'
import stringify from 'safe-stable-stringify'
import { BLOCK_DEF, REL_DEF } from './data-types'
import { getDerivedKids, getDerivedLinkageEntities } from './derived-relations-core'
import { RE_ANY_TAG_ONLY, TIPTAP_EMPTY } from './note3-regex-constants'
import { contentVlToPlaintext, parseBlockContentValue } from './note3-utils-nodeps'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

export function mapAndRecurseKids<R = void>(
	thread: ThreadOnlyCurrentNoDeleted,
	blockID: EntityID,
	mapKid: (kid: { relID: EntityID; blockID: EntityID; derived?: boolean }, depth: number) => R,
	joinResults: (blockResult: R, kidResults: R[]) => R,
) {
	const recurse = function recurseOuter(blockID: EntityID, relID: EntityID, trace: EntityID[], depth: number, derived = false) {
		// Recursion checks
		if (trace.includes(blockID)) {
			ERROR(`Kids loop`, { blockID, _relID: relID, _trace: trace } /* , 'fixIt:', function fixIt() {
				insertApplogsInAppDB({ en: relID, at: ENTITY_DEF.isDeleted, vl: true })
			} */)
			throw new Error(`[recursiveKids.loop] time travel?`)
		}
		if (trace.length > 42) {
			ERROR(`Depth limit reached`, trace)
			throw new Error(`[recursiveKidsCount.maxDepth] How deep can you go?`) // ? is 42 the answers.length)
		}

		// Process this block
		const result = mapKid({ blockID, relID, derived }, depth)

		// Process the children
		const kidsQuery = query(thread, [
			{ en: '?kidRelID', at: 'relation/childOf', vl: blockID },
			{ en: '?kidRelID', at: 'relation/block', vl: '?kidID' },
		])
		const kidResults = kidsQuery.records.map(function kidsQueryRecordsInnerMapper({ kidID, kidRelID }) {
			return recurse(kidID as EntityID, kidRelID as EntityID, [...trace, blockID], depth + 1)
		})
		// Derived placements are "what's visible" — include them like real kids
		// (visible-consistent shares/exports, see derived-relations-core.ts)
		for (const derivedKidID of getDerivedKids(thread, blockID)) {
			if (trace.includes(derivedKidID) || derivedKidID === blockID) continue
			kidResults.push(recurse(derivedKidID, null, [...trace, blockID], depth + 1, true))
		}

		return joinResults(result, kidResults)
	}
	return recurse(blockID, null, [], 0)
}

export function getBlocksWithTags(thread: ThreadOnlyCurrentNoDeleted, tags: readonly string[]) {
	if (!tags.length) throw new Error(`[useBlocksWithTags] Empty tags list`)
	tags.forEach((tag) => {
		if (!RE_ANY_TAG_ONLY.exec(tag)) throw new Error(`[useBlocksWithTags] Tag too short: '${tag}'`)
	})
	const blocks = queryAndMap(thread, [{
		en: '?blockID',
		at: 'block/content',
		vl: vl =>
			tags.every((tag) => {
				return !!plainContentMatchSpecificTag(contentVlToPlaintext(vl as string), tag)
			}),
	}], 'blockID') as EntityID[] // TODO: serializable REGEXSearchContext.
	return blocks
}

export function blockThreadWithRecursiveKids(thread: Thread, blockID: EntityID): MappedThread {
	const currentThread = withoutDeleted(lastWriteWins(thread)) as ThreadOnlyCurrentNoDeleted // HACK: how to do actual withHistory?

	const mappedKids = mapAndRecurseKids(currentThread, blockID, ({ blockID, relID, derived }, depth) => {
		// NB: `at` must be a Set (built via anyOf(...)) for OR-of-values matching.
		// Bare arrays fall into `matchPartStatic`'s deep-equality branch and never match single `at` values,
		// so `{ at: BLOCK_DEF._attrsFull }` would silently return zero results even when matching logs exist.
		const blockLogsWithHistory = query(thread, [
			{ en: blockID, at: anyOf(...BLOCK_DEF._attrsFull) },
		])
		if (blockLogsWithHistory.isEmpty) {
			// Top-level missing block is a hard error: callers (e.g. quickShareBlock) need a clear signal
			// rather than a silently empty thread that they can't distinguish from "block has no applogs".
			// Deeper-level missing kids are warned-and-skipped (preserves prior behaviour for partial trees).
			if (depth === 0) {
				ERROR(`threadRecurse encountered missing block at root`, { blockID, relID })
				throw new Error(`Block ${blockID} has no applogs in the current thread (not loaded or already deleted)`)
			}
			WARN(`threadRecurse encountered missing block - skipping`, { blockID, relID })
			return null
		}
		if (derived) {
			// Derived placement: include the FULL entity (block attrs + the
			// structuredTranscript/* linkage) and its transcription entities —
			// without those, the receiving side can't resolve the anchor and
			// the block would arrive as an invisible orphan.
			const allEntityLogs = query(thread, [{ en: blockID }])
			const linkageThreads = getDerivedLinkageEntities(currentThread, blockID)
				.map(trxID => query(thread, [{ en: trxID }]).threadOfAllTrails)
			return joinThreads([allEntityLogs.threadOfAllTrails, ...linkageThreads])
		}
		let relLogsWithHistory
		if (relID) {
			relLogsWithHistory = query(thread, [
				{ en: relID, at: anyOf(...REL_DEF._attrsFull) },
			])
			if (relLogsWithHistory.isEmpty) {
				throw ERROR(`threadRecurse encountered missing relation (how did we get here?!)`, { blockID, relID })
			}
		}
		if (!relID) return blockLogsWithHistory.threadOfAllTrails
		return joinThreads([
			blockLogsWithHistory.threadOfAllTrails,
			relLogsWithHistory.threadOfAllTrails,
		])
	}, (blockThread, kidThreads) => {
		return joinThreads([...(blockThread ? [blockThread] : []), ...kidThreads])
	})
	DEBUG('afterMappingKids', { mappedKids })
	return mappedKids
}

export const TIPTAP_EMPTY_SERIALIZED = serializeTiptapToVl(TIPTAP_EMPTY)

export function compareBlockContent(contentA: TiptapContent, contentB) {
	if (stringify(contentA) === stringify(contentB)) return true
	if (stringify(contentA) === stringify(TIPTAP_EMPTY) && !contentB) return true // HACK suppress setting initial content if anyways nothing set yet
	return false
}
export function serializeTiptapToVl(tiptap: TiptapContent) {
	return stringify(tiptap)
}

export function onlyFromAgent(
	thread: Thread,
	ag: string,
) {
	return rollingFilter(thread, { ag })
}

export function sanityCheckLogs(applogs: any) {
	DEBUG('sanityCheckLogs', { applogs })
	return removeDuplicateAppLogs(
		applogs.map((log) => {
			if (isEncryptedApplog(log)) {
				VERBOSE('encrypted log', log)
				return log
			}
			return { ...log, pv: log.pv ?? null } // HACK: tolerate old logs without pv
		}),
		'safety',
	)
}
export function replaceInTiptap(content: TiptapContent, needle: string | RegExp, replace: string) {
	// HACK json replace...
	const newContent = serializeTiptapToVl(content).replace(needle, replace)
	return parseBlockContentValue(newContent)
}
